import React from 'react';
import { Empty, Typography } from 'antd';
import { Cell, Pie, PieChart, ResponsiveContainer } from 'recharts';
import { formatCount, resolveSuccessRate } from '../../utils';

interface Props {
  successCount: number | null;
  failedCount: number | null;
}

const SUCCESS_COLOR = '#52c41a';
const FAILED_COLOR = '#ff4d4f';

/**
 *  발송 성공률 도넛 차트.
 *  성공/실패 집계가 모두 없으면(= 둘 다 null, 혹은 합이 0) 집계 없음 상태를 노출한다.
 *  (스펙: summary 가 없는 발송은 숫자를 0 으로 단정하지 않는다)
 *  성공률은 실패가 있으면 99% 를 넘지 않게 내림한다 (calculateSuccessRate).
 *  성공/실패 중 하나라도 null 이면 성공률을 계산하지 않고 '-' 로 표기한다 (resolveSuccessRate).
 */
const AlimtalkSendResultDonutChart = ({ successCount, failedCount }: Props): JSX.Element => {
  const hasData = successCount !== null || failedCount !== null;
  const success = successCount ?? 0;
  const failed = failedCount ?? 0;
  const total = success + failed;

  if (!hasData || total === 0) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 220 }}>
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="성공/실패 집계 없음" />
      </div>
    );
  }

  const successRate = resolveSuccessRate(successCount, failedCount);
  const data = [
    { name: '성공', value: success, count: successCount, color: SUCCESS_COLOR },
    { name: '실패', value: failed, count: failedCount, color: FAILED_COLOR },
  ];

  return (
    <div>
      <div style={{ position: 'relative', width: '100%', height: 220 }}>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={data}
              dataKey="value"
              nameKey="name"
              cx="50%"
              cy="50%"
              innerRadius={65}
              outerRadius={95}
              startAngle={90}
              endAngle={-270}
              stroke="none"
            >
              {data.map((entry) => (
                <Cell key={entry.name} fill={entry.color} />
              ))}
            </Pie>
          </PieChart>
        </ResponsiveContainer>
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            pointerEvents: 'none',
          }}
        >
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            성공률
          </Typography.Text>
          <Typography.Title level={2} style={{ margin: 0 }}>
            {successRate === null ? '-' : `${successRate}%`}
          </Typography.Title>
        </div>
      </div>

      <div style={{ display: 'flex', justifyContent: 'center', gap: 24, marginTop: 8 }}>
        {data.map((entry) => (
          <span key={entry.name} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <span
              style={{
                width: 10,
                height: 10,
                borderRadius: '50%',
                backgroundColor: entry.color,
                display: 'inline-block',
              }}
            />
            <Typography.Text>
              {entry.name} {entry.count === null ? '-' : `${formatCount(entry.count)}건`}
            </Typography.Text>
          </span>
        ))}
      </div>
    </div>
  );
};

export default AlimtalkSendResultDonutChart;
